import {computed, decorate, observable} from 'mobx';
import {faBagsShopping} from '@fortawesome/pro-solid-svg-icons';
import {BaseModel} from './base';
import i18n from '../i18n/i18n';
import {CustomPropertyTypes} from './customProperty';
import {displayableProperty} from './displayableProperty';
import {displayablePropertyParam} from './displayablePropertyParam';
import {transformation} from '../utils/transformations';
import {Batch} from './batch';
import {Material} from './material';
import {round} from '../utils/number';

const MAX_DECIMAL_PLACES = 3;

export class Order extends BaseModel {
  static faIcon = faBagsShopping;
  searchableProperties = ['no'];
  eventLogs = [];
  shiftBooks = [];

  customPropertyType = CustomPropertyTypes.Order;
  i18nPrefix = 'order';

  displayableProperties = [
    displayableProperty({
      key: 'no',
      title: i18n.t('order.model.attributes.no'),
      params: [displayablePropertyParam({path: 'no'})],
    }),
    displayableProperty({
      key: 'parentOrderNo',
      title: i18n.t('order.model.attributes.parentOrderNo'),
      params: [displayablePropertyParam({path: 'parentOrder.no'})],
    }),
    displayableProperty({
      key: 'unitOfMeasure',
      title: i18n.t('order.model.attributes.unitOfMeasure'),
      params: [displayablePropertyParam({path: 'unitOfMeasure.label'})],
    }),
    displayableProperty({
      key: 'state',
      title: i18n.t('order.model.attributes.stateLabel'),
      params: [displayablePropertyParam({path: 'state'})],
    }),
    displayableProperty({
      key: 'quantityIsShould',
      title: i18n.t('order.model.attributes.quantityIsShould'),
      params: [
        displayablePropertyParam({
          path: 'actualQuantity',
          transform: transformation.round(MAX_DECIMAL_PLACES),
          as: 'actualQuantity',
        }),
        displayablePropertyParam({
          path: 'plannedQuantity',
          transform: transformation.round(MAX_DECIMAL_PLACES),
          as: 'plannedQuantity',
        }),
        displayablePropertyParam({
          path: 'unitOfMeasure',
          transform: transformation.objectProperty('label'),
          as: 'unitOfMeasure',
        }),
      ],
      template: '{actualQuantity} / {plannedQuantity} {unitOfMeasure}',
    }),
    displayableProperty({
      key: 'scrapIsShould',
      title: i18n.t('order.model.attributes.scrapIsShould'),
      params: [
        displayablePropertyParam({
          path: 'actualScrap',
          transform: transformation.round(MAX_DECIMAL_PLACES),
          as: 'actualScrap',
        }),
        displayablePropertyParam({
          path: 'plannedScrap',
          transform: transformation.round(MAX_DECIMAL_PLACES),
          as: 'plannedScrap',
        }),
        displayablePropertyParam({
          path: 'unitOfMeasure',
          transform: transformation.objectProperty('label'),
          as: 'unitOfMeasure',
        }),
      ],
      template: '{actualScrap} / {plannedScrap} {unitOfMeasure}',
    }),
    displayableProperty({
      key: 'type',
      title: i18n.t('order.model.attributes.type'),
      params: [displayablePropertyParam({path: 'type'})],
    }),
    displayableProperty({
      key: 'info',
      title: i18n.t('order.model.attributes.info'),
      params: [displayablePropertyParam({path: 'info'})],
    }),
    displayableProperty({
      key: 'plannedQuantity',
      title: i18n.t('order.model.attributes.plannedQuantity'),
      params: [
        displayablePropertyParam({
          path: 'plannedQuantity',
          transform: transformation.round(MAX_DECIMAL_PLACES),
          as: 'plannedQuantity',
        }),
        displayablePropertyParam({
          path: 'unitOfMeasure',
          transform: transformation.objectProperty('label'),
          as: 'unitOfMeasure',
        }),
      ],
      template: '{plannedQuantity} {unitOfMeasure}',
    }),
    displayableProperty({
      key: 'actualQuantity',
      title: i18n.t('order.model.attributes.actualQuantity'),
      params: [
        displayablePropertyParam({
          path: 'actualQuantity',
          transform: transformation.round(MAX_DECIMAL_PLACES),
          as: 'actualQuantity',
        }),
        displayablePropertyParam({
          path: 'unitOfMeasure',
          transform: transformation.objectProperty('label'),
          as: 'unitOfMeasure',
        }),
      ],
      template: '{actualQuantity} {unitOfMeasure}',
    }),
  ];

  populateAttributesFromStore(rootStore) {
    super.populateAttributesFromStore(rootStore);
    this.material = rootStore.materialStore.getById(this.materialId);
    this.unitOfMeasure = rootStore.unitOfMeasurementStore.getById(this.unitOfMeasureId);
    this.operations = rootStore.operationStore.operations.filter((op) => op.orderId === this.id);
    this.parentOrder = rootStore.orderStore.getById(this.parentOrderId);
    this.batch = rootStore.batchStore.getById(this.batchId);
    this.eventLogs = rootStore.eventLogStore.eventLogs.filter((eventLog) => eventLog.orderId === this.id);
    this.shiftBooks = rootStore.shiftBookStore.shiftBooks.filter((shiftBook) => shiftBook.orderId === this.id);
  }

  static allDisplayableProperties(rootStore, pathPrefix = '', titlePrefix = '') {
    const allDisplayableProperties = super.allDisplayableProperties(rootStore, pathPrefix, titlePrefix);
    allDisplayableProperties.push(
      ...this.displayableCustomProperties(rootStore, CustomPropertyTypes.Order, pathPrefix, titlePrefix)
    );
    const batchPrefix = `${titlePrefix}${i18n.t('batch.model.one')} > `;
    allDisplayableProperties.push(
      ...Batch.allDisplayableProperties(rootStore, `${pathPrefix}batch.`, batchPrefix)
    );
    const materialPrefix = `${titlePrefix}`;
    allDisplayableProperties.push(
      ...Material.allDisplayableProperties(rootStore, `${pathPrefix}material.`, materialPrefix)
    );

    return allDisplayableProperties;
  }

  get remainingQuantity() {
    return round(this.plannedQuantity - this.actualQuantity, MAX_DECIMAL_PLACES);
  }
}

decorate(Order, {
  id: observable,
  no: observable,
  material: observable,
  materialId: observable,
  unitOfMeasure: observable,
  unitOfMeasureId: observable,
  parentOrder: observable,
  parentOrderId: observable,
  operations: observable,
  properties: observable,
  state: observable,
  workflowId: observable,
  plannedQuantity: observable,
  actualQuantity: observable,
  plannedScrap: observable,
  actualScrap: observable,
  batch: observable,
  batchId: observable,
  type: observable,
  info: observable,
  isYieldReportingDisabled: observable,
  deletedAt: observable,
  deletedBy: observable,
  ...Order.modelChangeDecorations,
  remainingQuantity: computed,
});

/**
 * order states translations:
 * t('order.model.attributes.state.PLANNED')
 * t('order.model.attributes.state.READY')
 * t('order.model.attributes.state.RUNNING')
 * t('order.model.attributes.state.FINISHED')
 * t('order.model.attributes.state.LOCKED')
 * t('order.model.attributes.state.TERMINATED')
 * t('order.model.attributes.state.REPORTED')
 */
export const OrderState = Object.freeze({
  0: 'PLANNED',
  1: 'READY',
  2: 'RUNNING',
  3: 'FINISHED',
  4: 'LOCKED',
  5: 'TERMINATED',
  6: 'REPORTED',
});
